import {ProjectColors} from "../../../Enums/Core/projectColors";
import {Button, Col, Row, Spinner} from "react-bootstrap";
import DataMultiSelector from "../DataMultiSelector/DataMultiSelector";
import TimeScaleFilter from "../TimeScaleFilter/TimeScaleFilter";
import React, {useEffect, useState} from "react";
import WaterConsumptionAndAlertsCharts from "../WaterConsumptionAndAlertsCharts/WaterConsumptionAndAlertsCharts";
import {
    DAYS, HOURS, MAX_SELECTION_LIMIT, MONTHS, WEEKS
} from "../../../Constants/constants";
import DrillDownModal from "../DrillDownModal/DrillDownModal";
import {apolloClientReports} from "../../../GraphQL/config";
import {
    QUERY_GET_WATER_CONSUMPTION_AND_ALERTS
} from "../../../GraphQL/ReportGenerator/queries";
import {
    getDailyOffsetEndDate,
    getDailyOffsetStartDate,
    getEndDate,
    getMonthlyEndDate,
    getMonthlyOffsetEndDate,
    getMonthlyStartDate,
    getMonthlyOffsetStartDate,
    getResolutionName,
    getWeeklyOffsetEndDate,
    getWeeklyOffsetStartDate,
    pushCsvDates,
    setCsvHeaders,
    setDisabledOffsetButtons,
    buildOverTimeCategoriesData,
    getPointResolution,
} from "../Utils/utils";
import WaterConsumptionAndAlertsHighResolution from "./WaterConsumptionAndAlertsHighResolution";
import {BsFillSkipEndFill, BsFillSkipStartFill} from "react-icons/bs";
import convert, {DEFAULT_VOLUME_UNIT} from "../MeasurementText/MeasurementTextConverter";

export default function WaterConsumptionAndAlerts(props) {
    const defaultScale = MONTHS;
    const [isLoading, setIsLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [startDate, setStartDate] = useState(getMonthlyStartDate());
    const [endDate, setEndDate] = useState(getMonthlyEndDate());
    const [selectedDate, setSelectedDate] = useState();
    const [modalShow, setModalShow] = React.useState(false);
    const [barIndex, setBarIndex] = useState(defaultScale);
    const [resolution, setResolution] = useState(defaultScale);
    const [entities, setEntities] = useState();
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [categories, setCategories] = useState();
    const [clickable, setClickable] = useState(true);
    const colors = [ProjectColors.Pink, ProjectColors.SteelBlue, ProjectColors.DarkTeal, ProjectColors.Orange];
    const [chartData, setChartData] = useState();
    const [csvData, setCsvData] = useState([]);
    const [leftArrowDisabled, setLeftArrowDisabled] = useState(false);
    const [rightArrowDisabled, setRightArrowDisabled] = useState(true);
    const [volumeUnit, setVolumeUnit] = useState(DEFAULT_VOLUME_UNIT);
    const entityType = props.entityLabel;

    const getWaterConsumptionAndAlertsFromDB = async () => {
        await apolloClientReports.query({
            query: QUERY_GET_WATER_CONSUMPTION_AND_ALERTS, variables: {
                resolution: getResolutionName(resolution),
                from: startDate.toISOString().split('T')[0],
                to: endDate.toISOString().split('T')[0],
                entityType: entityType
            }
        }).then(res => {
            let response = res?.data?.waterConsumptionAndLeakAlertsHistogram;
            if (!response) {
                console.error(`Data from getLeakVolumeOverTimeFromDB is empty`);
            } else {
                setVolumeUnit(convert(response.measureUnit.volumeUnit));
                setEntities(response?.entities?.map((entity) => entity));
                if (selectedEntities?.length === 0) {
                    setSelectedEntities(response?.entities?.map((entity) => entity).slice(0, MAX_SELECTION_LIMIT));
                } else {
                    let selectedEntitiesIds = selectedEntities?.map((entity) => entity.id);
                    let usedEntities = response?.entities?.map((entity) => {
                        if (selectedEntitiesIds.includes(entity.id)) {
                            return entity
                        }
                    }).filter((entity) => entity !== undefined);
                    setSelectedEntities(usedEntities);
                }
                setCsvData(buildCsvData(response.entities));
                response.entities[0] ? setDisabledOffsetButtons(response.entities[0], offset, setRightArrowDisabled, setLeftArrowDisabled, startDate, resolution) : offset < 0 ? setRightArrowDisabled(false) : setRightArrowDisabled(true);
                setIsLoading(false);
            }
        })
    }


    const buildCsvData = (responseData) => {
        let csvDataLocal = [];
        let headers = setCsvHeaders(resolution);

        responseData.forEach((entity) => {
            headers.push(`Leak volume - ${entity.name}`);
            headers.push(`Alerts - ${entity.name}`);
        });
        csvDataLocal.push(headers);

        responseData[0]?.data?.forEach((entityData, index) => {
            let row = [index + 1];
            row = pushCsvDates(resolution, entityData.date, row);
            csvDataLocal.push(row);
        })

        responseData?.forEach((entity) => {
            entity?.data?.forEach((entityData, index) => {
                csvDataLocal[index + 1].push(entityData.waterConsumption);
                csvDataLocal[index + 1].push(entityData.leakAlerts);
            })
        })

        return csvDataLocal;
    }

    useEffect(() => {
        setIsLoading(true);
        getWaterConsumptionAndAlertsFromDB();
    }, [startDate, endDate]);

    useEffect(() => {
        switch (resolution) {
            case MONTHS:
                setStartDate(getMonthlyOffsetStartDate(offset));
                setEndDate(getMonthlyOffsetEndDate(offset));
                break;
            case WEEKS:
                setStartDate(getWeeklyOffsetStartDate(offset));
                setEndDate(getWeeklyOffsetEndDate(offset));
                break;
            case DAYS:
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
        }
    }, [offset]);

    useEffect(() => {
        let now = new Date();
        if (offset !== 0) setOffset(0);
        switch (resolution) {
            case HOURS:
                setClickable(false);
                setStartDate(props.selectedDate || now);
                setEndDate(getEndDate(props.selectedDate, resolution));
                break;
            case DAYS:
                setClickable(false);
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
            case WEEKS:
                setClickable(true);
                setStartDate(getWeeklyOffsetStartDate(offset));
                setEndDate(getWeeklyOffsetEndDate(offset));
                break;
            default:
                setClickable(true);
                setStartDate(props.selectedDate || getMonthlyStartDate());
                setEndDate(now);
                break;
        }
    }, [resolution]);

    useEffect((e) => {
        if (selectedEntities) {
            setCategories(buildOverTimeCategoriesData(selectedEntities, resolution));
        }
    }, [selectedEntities]);

    useEffect(() => {
        setChartData(selectedEntities?.map((a, index) => {
            return {
                name: a.name,
                consumptionData: a.data?.map((dat) => dat.waterConsumption),
                leakAlerts: a.data?.map((dat) => dat.leakAlerts),
                color: colors[index]
            }
        }));
    }, [categories]);

    return (<div>
        <Row>
            <Col md={12}>
                <p className={'card-title-main bold-title'}>{props.title}</p>
            </Col>
            <Col md={2}>
                <DataMultiSelector
                    variant={props.variant}
                    setSelectedValues={setSelectedEntities}
                    selectionLimit={MAX_SELECTION_LIMIT}
                    entities={entities?.map((entity) => {
                        return {...entity, value: entity.value, label: entity.name}
                    })}
                    selectedValues={selectedEntities?.map((entity) => {
                        return {...entity, value: entity.value, label: entity.name}
                    })}/>
            </Col>
            <Col md={{offset: 7, span: 3}}>
                <TimeScaleFilter
                    setOffSet={setOffset}
                    hasExportCsv
                    setScale={setResolution}
                    defaultScale={resolution}
                    className={'time-scale-filter-small-card'}
                    csvData={csvData}
                    title={`Water consumption and alerts over time (compare ${entityType}`}/>
            </Col>
        </Row>
        <br/>
        {isLoading && <Spinner animation="border" variant="primary"/>}
        {!isLoading && <>
            <Row>
                <Col md={1}><Button
                    className={'offset_button_large'}
                    disabled={leftArrowDisabled}
                    onClick={() => setOffset(offset - 1)}> <BsFillSkipStartFill size={30} color={'#686B73'}/>
                </Button>
                </Col>
                <Col md={10}>
                    {clickable ? <WaterConsumptionAndAlertsCharts
                        volumeUnit={volumeUnit}
                        categories={categories}
                        resolution={resolution}
                        data={chartData}
                        onClick={(event) => {
                            getPointResolution(event, resolution, setSelectedDate, setBarIndex, setModalShow)
                        }}
                    /> : <WaterConsumptionAndAlertsCharts
                        volumeUnit={volumeUnit}
                        categories={categories}
                        resolution={resolution}
                        data={chartData}
                    />}
                </Col>
                <Col md={1}><Button
                    className={'offset_button_large'}
                    disabled={rightArrowDisabled}
                    onClick={() => setOffset(offset + 1)}> <BsFillSkipEndFill size={30} color={'#686B73'}/>
                </Button>
                </Col>
            </Row>
            <DrillDownModal
                show={modalShow}
                onHide={() => setModalShow(false)}
                resolution={resolution}
                type={WaterConsumptionAndAlertsHighResolution}
                barIndex={barIndex || 0}
                selectedEntities={selectedEntities}
                variant={props.variant}
                selectedDate={selectedDate}
                colors={colors}
            />
        </>}
    </div>)
}
